// Single-page accordion mega form orchestrator
// Single-expand: only one section open at a time, with unsaved changes modal
import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useFormState } from 'react-hook-form';
import { FormModeProvider } from '../context/FormModeContext';
import { StartupFormProvider, useStartupForm } from '../context/StartupFormContext';
import { FieldMetadataProvider, useFieldMetadata } from '../context/FieldMetadataContext';
import { SectionAccordionItem } from './SectionAccordionItem';
import type { RenderFieldFn } from './SectionAccordionItem';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { StatusBar } from './StatusBar';
import type { StatusConfig } from './StatusBar';
import { FormInstructionBanner } from './FormInstructionBanner';
import type { FormMode } from '../context/FormModeContext';
import type { SectionConfig } from '../types/sections';
import type { StartupFormData } from '../context/StartupFormContext';
import type { FieldMetadataMap, FieldMetadata, FieldStatus } from '../types/fields';

// Optional tab grouping — sections are assigned to a group via getSectionGroupId. Sections
// that don't match any configured group id still render (under otherGroupLabel) instead of
// silently disappearing, so a newly created backend section always has a visible home even
// before its group assignment is configured.
export interface SectionGroupConfig {
  id: string;
  label: string;
}

const OTHER_GROUP_ID = '__other__';

// Field status config — aligned with FieldStatusDropdown colors and i18n keys
const FIELD_STATUS_CONFIG: StatusConfig[] = [
  { value: 'in_progress', color: 'bg-[#FFDD53]', labelKey: 'fieldStatus.in_progress' },
  { value: 'ai_review', color: 'bg-[#42BEFC]', labelKey: 'fieldStatus.ai_review' },
  { value: 'marked_for_review', color: 'bg-[#EF7700]', labelKey: 'fieldStatus.marked_for_review' },
  { value: 'validated', color: 'bg-[#20BD5A]', labelKey: 'fieldStatus.validated' },
  { value: 'incomplete', color: 'bg-[#A8A8A8]', labelKey: 'fieldStatus.incomplete' },
];

const EMPTY_STATUS_COUNTS: Record<FieldStatus, number> = {
  in_progress: 0,
  ai_review: 0,
  marked_for_review: 0,
  validated: 0,
  incomplete: 0,
};

const EMPTY_EXCLUDED_FIELD_NAMES: ReadonlySet<string> = new Set();

// Counts fields per status across all sections; fields with no metadata default to incomplete.
// excludedFieldNames skips fields rendered elsewhere (e.g. hero identity fields) so they don't
// inflate a tally the user never sees as part of the form body.
function useStatusCounts(
  sections: SectionConfig[],
  excludedFieldNames: ReadonlySet<string> = EMPTY_EXCLUDED_FIELD_NAMES
): Record<FieldStatus, number> {
  const { getAllMetadata } = useFieldMetadata();

  return useMemo(() => {
    const metadata = getAllMetadata();
    const allFieldNames = sections
      .flatMap((s) => s.fields.map((f) => f.name))
      .filter((name) => !excludedFieldNames.has(name));

    const counts: Record<FieldStatus, number> = { ...EMPTY_STATUS_COUNTS };

    for (const fieldName of allFieldNames) {
      const status = metadata[fieldName]?.status ?? 'incomplete';
      counts[status]++;
    }

    return counts;
  }, [sections, getAllMetadata, excludedFieldNames]);
}

// Counts fields per status per section; keyed by sectionId
function useSectionStatusCounts(
  sections: SectionConfig[],
  excludedFieldNames: ReadonlySet<string> = EMPTY_EXCLUDED_FIELD_NAMES
): Map<string, Record<FieldStatus, number>> {
  const { getAllMetadata } = useFieldMetadata();

  return useMemo(() => {
    const metadata = getAllMetadata();
    const result = new Map<string, Record<FieldStatus, number>>();

    for (const section of sections) {
      const counts: Record<FieldStatus, number> = { ...EMPTY_STATUS_COUNTS };
      for (const field of section.fields) {
        if (excludedFieldNames.has(field.name)) continue;
        const status = metadata[field.name]?.status ?? 'incomplete';
        counts[status]++;
      }
      result.set(section.id, counts);
    }

    return result;
  }, [sections, getAllMetadata, excludedFieldNames]);
}

// Accordion state context (internal to StartupProfileForm)
// Single-expand: only one section open at a time
interface AccordionState {
  expandedSections: Set<string>; // kept as Set for API compat, but only 0-1 items
  activeField: { sectionId: string; fieldName: string } | null;
}

interface AccordionContextValue extends AccordionState {
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
  collapseSection: (sectionId: string) => void;
  openField: (sectionId: string, fieldName: string) => void;
  closeField: () => void;
  // ID of the first section (dynamic, from API — not a static string)
  firstSectionId: string;
  // Page-level edit toggle (opt-in via StartupProfileForm's globalEditToggle prop). When
  // isGlobalEditFormMode is false, this is a no-op layer and every consumer keeps the
  // original single-field/per-section click-to-expand-and-save behavior untouched.
  isGlobalEditFormMode: boolean;
  isEditingAll: boolean;
  hasAnyDirty: boolean;
  isSavingAll: boolean;
  startEditingAll: () => void;
  saveAllDirty: () => Promise<void>;
  discardAllDirty: () => void;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

// Hook to access accordion state
export function useAccordion(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error('useAccordion must be used within StartupProfileForm');
  }
  return ctx;
}

// Props for StartupProfileForm
export interface StartupProfileFormProps {
  // Core
  mode: FormMode;
  sections: SectionConfig[];
  defaultValues?: Partial<StartupFormData>;

  // Section-level save (receives sectionId and current values for that section).
  // May resolve with a map of repeater field name -> item index -> newly-created backend
  // instance id, when the section held a repeater field with brand-new (unsaved) items — the
  // caller can't write into the RHF form itself, so it hands the ids back here to be applied
  // via setValue. Without this, a repeater item that was just created stays without an
  // __instanceId in the live form state, so the *next* save (in the same session, no page
  // reload) treats it as new again: re-creates a duplicate instance and, worse, reads the
  // field's own already-persisted instance as "removed" (present in the backend snapshot but
  // absent — because it's untagged — from the live form values) and deletes it.
  onSaveSection?: (
    sectionId: string,
    values: Record<string, any>
  ) => Promise<void | Record<string, Record<number, string>>>;

  // Legacy global save (for backwards compatibility)
  onSave?: (data: StartupFormData) => Promise<void>;

  // Called when a conditional field is hidden — caller should delete the BE value
  onFieldHidden?: (fieldName: string, valueId: string) => Promise<void>;

  // Field metadata
  fieldMetadata?: FieldMetadataMap;
  onFieldMetadataChange?: (fieldName: string, metadata: FieldMetadata) => void;

  // AI events (handled by web layer)
  onAIRegenerate?: (fieldName: string, currentValue: string) => void;
  // Triggers AI rephrase for all AI-enabled fields in a section
  onAISectionRegenerate?: (sectionId: string) => Promise<void>;
  // Whether the AI agent has enough context to regenerate (disables button when false)
  canAIRegenerate?: boolean;

  // Customization slots
  renderDocumentsHeader?: () => ReactNode;
  renderSectionHeader?: (
    section: SectionConfig,
    progress: number,
    isExpanded: boolean
  ) => ReactNode;
  // Top row content (startup name + reference doc) rendered inside the shared header card
  // Placed above the QuestionStatusBar; both share the same card container.
  // Receives the live (form.watch()-based) global progress so callers can
  // render an overall ProgressBar that updates as the user types, instead of
  // recomputing their own from a stale snapshot.
  renderTopRow?: (globalProgress: number) => ReactNode;

  // Overrides the wrapper classes around the section list — opt-in only, defaults to the
  // existing single-column "sections-list space-y-3" stack so other callers (onboarding,
  // admin/hub read-only modals) are unaffected. Pass e.g. a shared card className with
  // divide-y to render all sections as collapsible rows inside one container.
  sectionsContainerClassName?: string;
  // Opt-in overrides forwarded to each SectionAccordionItem — pairs with
  // sectionsContainerClassName to strip the per-section card chrome (bg/rounded/border) when
  // the caller's container already provides it, so sections read as rows in one shared card
  // instead of a grid of separate cards. Omit for the existing standalone-card look.
  sectionItemClassName?: string;
  sectionItemHeaderClassName?: string;

  // Optional tab grouping (opt-in — omit for the existing flat single-list behavior).
  // sectionGroups defines the tabs in order; getSectionGroupId assigns each section to one
  // by returning a group id from sectionGroups (or null/unmatched → grouped under
  // otherGroupLabel so unmapped sections stay visible instead of disappearing). Keeping the
  // mapping entirely in the caller (a plain array + resolver function) is what makes it easy
  // to move a section to another tab or add a new one later: edit the caller's config, not
  // this component.
  sectionGroups?: SectionGroupConfig[];
  getSectionGroupId?: (section: SectionConfig) => string | null;
  otherGroupLabel?: string;

  // Opt-in override for a single section's fields body — pairs with SectionAccordionItem's
  // own renderFields prop, just threaded through the public API so callers don't need to
  // reach into internals. Return undefined for a given section to fall back to the default
  // divide-y stack (e.g. a caller that only customizes one section by id/fieldName check).
  renderSectionFields?: (
    section: SectionConfig,
    renderField: RenderFieldFn
  ) => ReactNode | undefined;

  // Opt-in: field names to skip in the status bar, global progress, per-section progress ring,
  // and per-tab "filled/total" counts — for fields rendered outside the form body (e.g. hero
  // identity fields moved above the accordion via renderTopRow) that would otherwise inflate
  // completion stats for content the user never sees as a question. Omit for the existing
  // behavior of counting every field in `sections`.
  progressExcludedFieldNames?: string[];

  // Opt-in: page-level Edit/View toggle instead of per-field/per-section click-to-expand.
  // When true — reachable only via useAccordion()'s isEditingAll/startEditingAll/saveAllDirty/
  // discardAllDirty, driven by whatever the caller renders (e.g. hero buttons via renderTopRow)
  // — every section in the active tab renders fully expanded and every field shows its editor
  // at once instead of one at a time, the per-section Save/Discard footer stops rendering
  // (AI section-rephrase stays), and saveAllDirty/discardAllDirty act across ALL sections, not
  // just the visible tab. Omit (default) for the existing per-field/per-section click-to-open
  // accordion used by admin/hub/onboarding.
  globalEditToggle?: boolean;
}

// Inner content with access to form context
function StartupProfileFormContent({
  sections,
  onSaveSection,
  onAIRegenerate,
  onAISectionRegenerate,
  canAIRegenerate,
  renderDocumentsHeader,
  renderSectionHeader,
  renderTopRow,
  sectionsContainerClassName,
  sectionItemClassName,
  sectionItemHeaderClassName,
  sectionGroups,
  getSectionGroupId,
  otherGroupLabel,
  renderSectionFields,
  progressExcludedFieldNames,
  globalEditToggle,
  isReadOnly,
}: Pick<
  StartupProfileFormProps,
  | 'sections'
  | 'onSaveSection'
  | 'onAIRegenerate'
  | 'onAISectionRegenerate'
  | 'canAIRegenerate'
  | 'renderDocumentsHeader'
  | 'renderSectionHeader'
  | 'renderTopRow'
  | 'sectionsContainerClassName'
  | 'sectionItemClassName'
  | 'sectionItemHeaderClassName'
  | 'sectionGroups'
  | 'getSectionGroupId'
  | 'otherGroupLabel'
  | 'renderSectionFields'
  | 'progressExcludedFieldNames'
  | 'globalEditToggle'
> & { isReadOnly: boolean }) {
  const { form, discardChanges } = useStartupForm();
  const { getMetadata, setStatus } = useFieldMetadata();
  // form.formState.dirtyFields (read ad hoc off the context's form object) is a stale snapshot
  // unless something actually subscribes to it via a hook — useFormState is react-hook-form's
  // dedicated API for that, and is what keeps isSectionDirty/hasAnyDirty/handleSectionSave
  // reading live dirty state instead of whatever happened to be captured at a past render.
  const { dirtyFields } = useFormState({ control: form.control });
  const excludedFieldNames = useMemo(
    () => new Set(progressExcludedFieldNames ?? []),
    [progressExcludedFieldNames]
  );
  const statusCounts = useStatusCounts(sections, excludedFieldNames);
  const sectionStatusCounts = useSectionStatusCounts(sections, excludedFieldNames);

  // Watch all form values so progress recalculates reactively — declared early since both
  // section progress and the tab grouping's per-tab stats below depend on it.
  const watchedValues = form.watch();

  // Tab grouping (opt-in). Sections that don't match any configured group land in a
  // synthetic "other" group instead of disappearing — see SectionGroupConfig above.
  const { orderedGroups, sectionsByGroup, sectionIdToGroupId } = useMemo(() => {
    if (!sectionGroups || sectionGroups.length === 0) {
      return {
        orderedGroups: [] as SectionGroupConfig[],
        sectionsByGroup: new Map<string, SectionConfig[]>(),
        sectionIdToGroupId: new Map<string, string>(),
      };
    }

    const byGroup = new Map<string, SectionConfig[]>();
    sectionGroups.forEach((g) => byGroup.set(g.id, []));
    const idToGroup = new Map<string, string>();
    const other: SectionConfig[] = [];

    sections.forEach((section) => {
      const groupId = getSectionGroupId?.(section) ?? null;
      if (groupId && byGroup.has(groupId)) {
        byGroup.get(groupId)!.push(section);
        idToGroup.set(section.id, groupId);
      } else {
        other.push(section);
        idToGroup.set(section.id, OTHER_GROUP_ID);
      }
    });

    const groups = [...sectionGroups];
    if (other.length > 0) {
      byGroup.set(OTHER_GROUP_ID, other);
      groups.push({ id: OTHER_GROUP_ID, label: otherGroupLabel ?? 'Other' });
    }

    return { orderedGroups: groups, sectionsByGroup: byGroup, sectionIdToGroupId: idToGroup };
  }, [sectionGroups, getSectionGroupId, sections, otherGroupLabel]);

  const hasGroups = orderedGroups.length > 0;

  // Single-expand accordion state — first section starts expanded to orient the user on load
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(() =>
    hasGroups
      ? (sectionsByGroup.get(orderedGroups[0].id)?.[0]?.id ?? null)
      : (sections[0]?.id ?? null)
  );
  const [activeGroupId, setActiveGroupId] = useState<string | null>(
    () => orderedGroups[0]?.id ?? null
  );
  const [activeField, setActiveField] = useState<{ sectionId: string; fieldName: string } | null>(
    null
  );

  // Keep the active tab valid if the group config changes shape (e.g. locale reload adds/drops
  // an "other" bucket) — falls back to the first group rather than pointing at a stale id.
  useEffect(() => {
    if (!hasGroups) return;
    if (!orderedGroups.some((g) => g.id === activeGroupId)) {
      setActiveGroupId(orderedGroups[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedGroups, hasGroups]);

  // Sections rendered in the accordion list: all of them when ungrouped, or just the active
  // tab's when grouped.
  const visibleSections = hasGroups ? (sectionsByGroup.get(activeGroupId ?? '') ?? []) : sections;

  // Per-tab completion counts ("filled/total"), aggregated from the same live watchedValues
  // used for section-level progress below.
  const groupStats = useMemo(() => {
    const map = new Map<string, { filled: number; total: number }>();
    if (!hasGroups) return map;
    for (const group of orderedGroups) {
      const secs = sectionsByGroup.get(group.id) ?? [];
      let filled = 0;
      let total = 0;
      secs.forEach((section) => {
        section.fields.forEach((field) => {
          if (excludedFieldNames.has(field.name)) return;
          total++;
          const val = watchedValues[field.name];
          const isFilled = Array.isArray(val)
            ? val.length > 0
            : val !== undefined && val !== null && val !== '';
          if (isFilled) filled++;
        });
      });
      map.set(group.id, { filled, total });
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGroups, orderedGroups, sectionsByGroup, watchedValues, excludedFieldNames]);

  // A discarded field may be sitting on an unconfirmed AI-accept: AIComparisonPanel's
  // "Reemplazar" sets status to validated and persists it immediately (via
  // onFieldMetadataChange), decoupled from the value itself, which only saves on Guardar.
  // Discarding the value without also reverting the status left a stale "Respuesta validada"
  // badge on the field's old, un-reformulated value — both locally and, since setStatus here
  // round-trips through the same onFieldMetadataChange persistence, on the backend too, so a
  // subsequent refresh no longer resurrects the stale status either (OPA-307 bugfix).
  const revertPendingFieldStatus = useCallback(
    (fieldNames: string[]) => {
      fieldNames.forEach((name) => {
        const meta = getMetadata(name);
        if (meta.previousStatus) setStatus(name, meta.previousStatus);
      });
    },
    [getMetadata, setStatus]
  );

  // Modal state for unsaved changes warning
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Check if section has dirty fields. Deliberately NOT wrapped in useCallback: react-hook-form's
  // formState.dirtyFields Proxy keeps a stable object reference across renders while mutating its
  // internal content, so a dependency array keyed on that reference never sees a "change" and a
  // memoized version of this would silently go stale after the first edit. Recomputing on every
  // render is cheap (a few dozen sections / a few hundred fields) and always correct.
  //
  // Object.keys(dirtyFields) is NOT a dirty check: react-hook-form registers every field the
  // instant its accordion item expands (isEditingAll makes that happen for every field in every
  // section at once), and a merely-registered-but-untouched field still shows up as a key with
  // value `false`. A plain Set.has(field.name) on those keys would read every open section as
  // dirty regardless of whether the user changed anything. Checking for a genuinely truthy leaf
  // handles both plain fields (dirtyFields[name] === true) and repeater/array fields, where RHF
  // nests per-item-per-subfield booleans instead of a single boolean at the top.
  const isFieldValueDirty = (value: unknown): boolean => {
    if (value === true) return true;
    if (Array.isArray(value)) return value.some(isFieldValueDirty);
    if (value && typeof value === 'object') return Object.values(value).some(isFieldValueDirty);
    return false;
  };

  const isSectionDirty = (sectionId: string): boolean => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return false;

    return section.fields.some((field) =>
      isFieldValueDirty((dirtyFields as Record<string, unknown>)[field.name])
    );
  };

  // Global edit mode (opt-in via globalEditToggle) — page-level view/edit toggle that
  // supersedes the single-field/single-section accordion instead of removing it: view mode
  // still lets sections open/close for browsing, edit mode forces every section in the active
  // tab open with every field showing its editor at once (isSectionDirty/handleSectionSave/
  // handleDiscard below are reused, just looped across sections, rather than reimplemented).
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);

  // Sections the user manually collapsed while in global edit mode — edit mode still forces
  // every section open by default (nothing to save is lost by collapsing, the field values
  // stay mounted in react-hook-form regardless of accordion state), but a long profile with
  // every section expanded at once is hard to scan, so this lets the user fold sections they
  // aren't actively editing. Cleared whenever edit mode is (re-)entered or exited so it never
  // leaks a collapsed section into the next edit session or into view mode.
  const [collapsedWhileEditing, setCollapsedWhileEditing] = useState<Set<string>>(new Set());

  // Convert to Set for API compatibility. In global edit mode, every section in the visible
  // tab is forced open by default, except the ones the user explicitly collapsed.
  const expandedSections = useMemo(() => {
    if (globalEditToggle && isEditingAll) {
      return new Set(
        visibleSections.filter((s) => !collapsedWhileEditing.has(s.id)).map((s) => s.id)
      );
    }
    return expandedSectionId ? new Set([expandedSectionId]) : new Set<string>();
  }, [expandedSectionId, globalEditToggle, isEditingAll, visibleSections, collapsedWhileEditing]);

  // Section accordion controls - single expand with dirty check
  const toggleSection = useCallback(
    (sectionId: string) => {
      const targetGroupId = hasGroups ? (sectionIdToGroupId.get(sectionId) ?? null) : null;

      // Global edit mode: sections are already all forced open and saving is a single
      // page-level action, so there's nothing to guard — just keep the underlying "last
      // focused" section/tab in sync (harmless bookkeeping, has no visible effect while
      // isEditingAll stays true).
      if (globalEditToggle && isEditingAll) {
        if (targetGroupId && targetGroupId !== activeGroupId) setActiveGroupId(targetGroupId);
        setExpandedSectionId((prev) => (prev === sectionId ? null : sectionId));
        setCollapsedWhileEditing((prev) => {
          const next = new Set(prev);
          if (next.has(sectionId)) {
            next.delete(sectionId);
          } else {
            next.add(sectionId);
          }
          return next;
        });
        return;
      }

      // Closing current section
      if (expandedSectionId === sectionId) {
        setExpandedSectionId(null);
        setActiveField(null);
        return;
      }

      // Opening new section - check if current has unsaved changes
      if (expandedSectionId && isSectionDirty(expandedSectionId)) {
        setPendingSectionId(sectionId);
        setPendingGroupId(targetGroupId);
        setShowUnsavedModal(true);
        return;
      }

      // No dirty state, switch directly — jump tabs too if the target section lives elsewhere
      if (targetGroupId && targetGroupId !== activeGroupId) {
        setActiveGroupId(targetGroupId);
      }
      setExpandedSectionId(sectionId);
      setActiveField(null);
    },
    [
      expandedSectionId,
      isSectionDirty,
      hasGroups,
      sectionIdToGroupId,
      activeGroupId,
      globalEditToggle,
      isEditingAll,
    ]
  );

  // Tab switch controls - same dirty-check flow as toggleSection, but the target is a whole
  // group: lands on its first section so the accordion still orients the user on switch.
  const handleGroupChange = useCallback(
    (groupId: string) => {
      if (groupId === activeGroupId) return;

      const targetFirstSectionId = sectionsByGroup.get(groupId)?.[0]?.id ?? null;

      // Global edit mode: free navigation between tabs, no per-section dirty guard — the
      // single global Save button covers every touched section regardless of which tab is
      // visible when it's clicked.
      if (globalEditToggle && isEditingAll) {
        setActiveGroupId(groupId);
        setExpandedSectionId(targetFirstSectionId);
        setActiveField(null);
        return;
      }

      if (expandedSectionId && isSectionDirty(expandedSectionId)) {
        setPendingGroupId(groupId);
        setPendingSectionId(targetFirstSectionId);
        setShowUnsavedModal(true);
        return;
      }

      setActiveGroupId(groupId);
      setExpandedSectionId(targetFirstSectionId);
      setActiveField(null);
    },
    [
      activeGroupId,
      sectionsByGroup,
      expandedSectionId,
      isSectionDirty,
      globalEditToggle,
      isEditingAll,
    ]
  );

  // Field accordion controls (only one field open at a time)
  const openField = useCallback((sectionId: string, fieldName: string) => {
    // Auto-expand section if closed
    setExpandedSectionId(sectionId);
    setActiveField({ sectionId, fieldName });
  }, []);

  const closeField = useCallback(() => {
    setActiveField(null);
  }, []);

  // Save handler for section - validates then extracts values and calls callback
  const handleSectionSave = useCallback(
    async (sectionId: string, options?: { resetAfterSave?: boolean }) => {
      if (!onSaveSection) return;

      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      // Validate only this section's fields before saving
      const fieldNames = section.fields.map((f) => f.name);
      const isValid = await form.trigger(fieldNames);
      if (!isValid) {
        // Open the first field with an error so the user can see and fix it
        const errors = form.formState.errors;
        const firstErrorField = fieldNames.find((name) => errors[name]);
        if (firstErrorField) openField(sectionId, firstErrorField);
        return;
      }

      // Extract only dirty fields — untouched fields are skipped so their status isn't overwritten.
      // Repeater and file fields always included: repeaters need full list for removal diff,
      // files need full value to detect new File uploads vs existing URL strings.
      const allValues = form.getValues();
      const sectionValues: Record<string, any> = {};
      section.fields.forEach((field) => {
        const isSpecial = field.type === 'repeater' || field.type === 'file';
        if (isSpecial || !!dirtyFields[field.name]) {
          sectionValues[field.name] = allValues[field.name];
        }
      });

      const newInstanceIds = await onSaveSection(sectionId, sectionValues);

      // Tag newly-created repeater items with their backend instance id so a later save
      // (still in this session) recognizes them as existing instead of re-creating them —
      // see the onSaveSection doc comment above.
      if (newInstanceIds) {
        Object.entries(newInstanceIds).forEach(([fieldName, indexToId]) => {
          Object.entries(indexToId).forEach(([index, instanceId]) => {
            form.setValue(`${fieldName}.${index}.__instanceId` as any, instanceId, {
              shouldDirty: false,
            });
          });
        });
      }

      // Clear dirty state after successful save so unsaved changes modal doesn't trigger.
      // Skipped when saving multiple sections in one batch (saveAllDirty below) — resetting
      // here would wipe the dirty flags of sections not yet saved in that same loop.
      if (options?.resetAfterSave ?? true) {
        form.reset(form.getValues());
      }
    },
    [sections, form, onSaveSection, openField, dirtyFields]
  );

  // True when ANY section (not just the visible tab) has unsaved fields — drives the hero
  // Save button's disabled state and whether Discard has anything to revert. Plain value, not
  // memoized, for the same reason isSectionDirty isn't — see its comment above.
  const hasAnyDirty = sections.some((s) => isSectionDirty(s.id));

  const startEditingAll = useCallback(() => {
    setIsEditingAll(true);
    setCollapsedWhileEditing(new Set());
  }, []);

  // Saves every dirty section across the whole form (not just the active tab), in one batch,
  // then clears dirty state once at the end — see handleSectionSave's resetAfterSave note.
  const saveAllDirty = useCallback(async () => {
    if (!onSaveSection) return;
    const dirtySectionIds = sections.filter((s) => isSectionDirty(s.id)).map((s) => s.id);
    if (dirtySectionIds.length === 0) {
      setIsEditingAll(false);
      setCollapsedWhileEditing(new Set());
      return;
    }
    setIsSavingAll(true);
    try {
      for (const sectionId of dirtySectionIds) {
        await handleSectionSave(sectionId, { resetAfterSave: false });
      }
      form.reset(form.getValues());
      setIsEditingAll(false);
      setCollapsedWhileEditing(new Set());
    } finally {
      setIsSavingAll(false);
    }
  }, [sections, isSectionDirty, onSaveSection, handleSectionSave, form]);

  // Reverts every dirty section across the whole form, then exits edit mode — doubles as a
  // plain "cancel" when nothing is dirty.
  const discardAllDirty = useCallback(() => {
    sections
      .filter((s) => isSectionDirty(s.id))
      .forEach((s) => {
        const fieldNames = s.fields.map((f) => f.name);
        discardChanges(fieldNames);
        revertPendingFieldStatus(fieldNames);
      });
    setIsEditingAll(false);
    setCollapsedWhileEditing(new Set());
  }, [sections, isSectionDirty, discardChanges, revertPendingFieldStatus]);

  // Modal handlers
  const handleModalDiscard = useCallback(() => {
    // Reset section fields to last saved values
    if (expandedSectionId) {
      const section = sections.find((s) => s.id === expandedSectionId);
      if (section) {
        const fieldNames = section.fields.map((f) => f.name);
        discardChanges(fieldNames);
        revertPendingFieldStatus(fieldNames);
      }
    }
    // Switch to pending section (and tab, if it lives elsewhere)
    if (pendingGroupId) setActiveGroupId(pendingGroupId);
    setExpandedSectionId(pendingSectionId);
    setActiveField(null);
    setShowUnsavedModal(false);
    setPendingSectionId(null);
    setPendingGroupId(null);
  }, [
    expandedSectionId,
    pendingSectionId,
    pendingGroupId,
    sections,
    discardChanges,
    revertPendingFieldStatus,
  ]);

  const handleModalSave = useCallback(async () => {
    // Save current section then switch
    if (expandedSectionId) {
      try {
        await handleSectionSave(expandedSectionId);
      } catch {
        // Save failed (caller already showed the toast + reported to Sentry): keep the modal
        // open on the dirty section instead of switching and leaking an unhandled rejection.
        return;
      }
    }
    if (pendingGroupId) setActiveGroupId(pendingGroupId);
    setExpandedSectionId(pendingSectionId);
    setActiveField(null);
    setShowUnsavedModal(false);
    setPendingSectionId(null);
    setPendingGroupId(null);
  }, [expandedSectionId, pendingSectionId, pendingGroupId, handleSectionSave]);

  const handleModalClose = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingSectionId(null);
    setPendingGroupId(null);
  }, []);

  const expandSection = useCallback((sectionId: string) => {
    setExpandedSectionId(sectionId);
  }, []);

  const collapseSection = useCallback(
    (sectionId: string) => {
      if (expandedSectionId === sectionId) {
        setExpandedSectionId(null);
        setActiveField(null);
      }
    },
    [expandedSectionId]
  );

  // Overall completion % across all sections
  const globalProgress = useMemo(() => {
    const allFields = sections
      .flatMap((s) => s.fields)
      .filter((f) => !excludedFieldNames.has(f.name));
    if (allFields.length === 0) return 0;
    const filled = allFields.filter((f) => {
      const val = watchedValues[f.name];
      if (val === undefined || val === null || val === '') return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    }).length;
    return Math.round((filled / allFields.length) * 100);
  }, [sections, watchedValues, excludedFieldNames]);

  // Calculate section progress (filled / total fields)
  const getSectionProgress = useCallback(
    (section: SectionConfig): number => {
      const progressFields = section.fields.filter((f) => !excludedFieldNames.has(f.name));
      if (progressFields.length === 0) return 0;

      const filledCount = progressFields.filter((f) => {
        const val = watchedValues[f.name];
        if (val === undefined || val === null || val === '') return false;
        if (Array.isArray(val)) return val.length > 0;
        return true;
      }).length;

      return Math.round((filledCount / progressFields.length) * 100);
    },
    [watchedValues, excludedFieldNames]
  );

  const accordionValue = useMemo<AccordionContextValue>(
    () => ({
      expandedSections,
      activeField,
      toggleSection,
      expandSection,
      collapseSection,
      openField,
      closeField,
      firstSectionId: sections[0]?.id ?? '',
      isGlobalEditFormMode: !!globalEditToggle,
      isEditingAll: !!globalEditToggle && isEditingAll,
      hasAnyDirty,
      isSavingAll,
      startEditingAll,
      saveAllDirty,
      discardAllDirty,
    }),
    [
      expandedSections,
      activeField,
      toggleSection,
      expandSection,
      collapseSection,
      openField,
      closeField,
      sections,
      globalEditToggle,
      isEditingAll,
      hasAnyDirty,
      isSavingAll,
      startEditingAll,
      saveAllDirty,
      discardAllDirty,
    ]
  );

  // Discard handler for section - resets to last saved values
  const handleDiscard = useCallback(
    (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (section) {
        const fieldNames = section.fields.map((f) => f.name);
        discardChanges(fieldNames);
        revertPendingFieldStatus(fieldNames);
      }
    },
    [sections, discardChanges, revertPendingFieldStatus]
  );

  return (
    <AccordionContext.Provider value={accordionValue}>
      <div className="mega-form space-y-4">
        {/* Legacy documents header slot */}
        {renderDocumentsHeader?.()}

        {/* Unified header card: top row (name + docs) + question status bar */}
        {/* Both share one container so QSB has access to FieldMetadataContext */}
        <div className="bg-[#1a1a1a] border border-white/5 rounded-xl overflow-hidden">
          {/* Top row — provided by parent (startup name left, reference doc right) */}
          {renderTopRow?.(globalProgress)}

          {/* Horizontal separator — inset with mx-6 to align with content padding */}
          {/*renderTopRow && <div className="mx-6" />*/}

          {/* Question status bar — embedded, with generous vertical padding */}
          <div className="px-6 pt-5 pb-6">
            <StatusBar
              statuses={FIELD_STATUS_CONFIG}
              counts={statusCounts}
              titleKey="questionStatus.title"
              embedded
            />
          </div>
        </div>

        {/* Instruction banner — explains form purpose and Validated Answer status */}
        <FormInstructionBanner completionPercent={globalProgress} />

        {/* Tab bar — only rendered when the caller opts into sectionGroups */}
        {hasGroups && (
          <div role="tablist" className="flex flex-wrap gap-2 border-b border-white/5 pb-2">
            {orderedGroups.map((group) => {
              const stats = groupStats.get(group.id);
              const isActive = group.id === activeGroupId;
              return (
                <button
                  key={group.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleGroupChange(group.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#42BEFC]/20 text-[#19A2F8]'
                      : 'text-[#4D4D4D] hover:text-white/70'
                  }`}
                >
                  {group.label}
                  {stats && stats.total > 0 && (
                    <span className="ml-1.5 opacity-70">
                      {stats.filled}/{stats.total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Section accordions */}
        <div className={sectionsContainerClassName ?? 'sections-list space-y-3'}>
          {visibleSections.map((section) => (
            <SectionAccordionItem
              key={section.id}
              section={section}
              progress={getSectionProgress(section)}
              statuses={FIELD_STATUS_CONFIG}
              sectionCounts={sectionStatusCounts.get(section.id) ?? EMPTY_STATUS_COUNTS}
              onSave={
                onSaveSection && !globalEditToggle ? () => handleSectionSave(section.id) : undefined
              }
              onDiscard={
                isReadOnly || globalEditToggle ? undefined : () => handleDiscard(section.id)
              }
              onAIRegenerate={onAIRegenerate}
              onAISectionRegenerate={
                onAISectionRegenerate ? () => onAISectionRegenerate(section.id) : undefined
              }
              canAIRegenerate={canAIRegenerate}
              renderHeader={renderSectionHeader}
              className={sectionItemClassName}
              headerClassName={sectionItemHeaderClassName}
              renderFields={renderSectionFields}
            />
          ))}
        </div>
      </div>

      {/* Unsaved changes modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onDiscard={handleModalDiscard}
        onSave={handleModalSave}
        onClose={handleModalClose}
      />
    </AccordionContext.Provider>
  );
}

// Main StartupProfileForm component
export function StartupProfileForm({
  mode,
  sections,
  defaultValues,
  onSaveSection,
  onSave,
  onFieldHidden,
  fieldMetadata,
  onFieldMetadataChange,
  onAIRegenerate,
  onAISectionRegenerate,
  canAIRegenerate,
  renderDocumentsHeader,
  renderSectionHeader,
  renderTopRow,
  sectionsContainerClassName,
  sectionItemClassName,
  sectionItemHeaderClassName,
  sectionGroups,
  getSectionGroupId,
  otherGroupLabel,
  renderSectionFields,
  progressExcludedFieldNames,
  globalEditToggle,
}: StartupProfileFormProps) {
  // Fallback save for backwards compatibility
  const handleSave = onSave || (async () => {});
  const isReadOnly = mode === 'view';

  return (
    <FormModeProvider mode={mode}>
      <StartupFormProvider
        sections={sections}
        defaultValues={defaultValues}
        onSave={handleSave}
        onFieldHidden={onFieldHidden}
      >
        <FieldMetadataProvider
          initialMetadata={fieldMetadata}
          onMetadataChange={onFieldMetadataChange}
        >
          <StartupProfileFormContent
            sections={sections}
            onSaveSection={onSaveSection}
            onAIRegenerate={onAIRegenerate}
            onAISectionRegenerate={onAISectionRegenerate}
            canAIRegenerate={canAIRegenerate}
            renderDocumentsHeader={renderDocumentsHeader}
            renderSectionHeader={renderSectionHeader}
            renderTopRow={renderTopRow}
            sectionsContainerClassName={sectionsContainerClassName}
            sectionItemClassName={sectionItemClassName}
            sectionItemHeaderClassName={sectionItemHeaderClassName}
            sectionGroups={sectionGroups}
            getSectionGroupId={getSectionGroupId}
            otherGroupLabel={otherGroupLabel}
            renderSectionFields={renderSectionFields}
            progressExcludedFieldNames={progressExcludedFieldNames}
            globalEditToggle={globalEditToggle}
            isReadOnly={isReadOnly}
          />
        </FieldMetadataProvider>
      </StartupFormProvider>
    </FormModeProvider>
  );
}
